import React, { RefObject } from 'react';
import { connect } from 'react-redux';
import { Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { IReviewBookState } from '@store/slices';
import { RoomInfo } from '../room-info';
import { RootState } from '@share/utils';

import './style.scss';
import { debounce } from 'lodash';

interface IMapStateToProps {
  reviewBookStore: IReviewBookState;
}

interface IProps extends IMapStateToProps, WrappedComponentProps {
  formRef: RefObject<FormInstance>;
  isCheckMainGuest: boolean;
  handleGuestFormValidation: (guestForm: boolean) => void;
  emailRef: RefObject<FormInstance>;
  isEmailChanging: (isEmailChanging: string | undefined) => void;
}

interface IState {
  isFormValid: boolean;
}


class GuestInfoComponent extends React.Component<IProps> {
  mainGuestData = (item: string): string => {
    const guest = this.props.reviewBookStore.guests.find((item) => item.isMainGuest === true);

    return guest ? (guest[item] as string) : '';
  };

  onFocusPhone = (): void => {
    this.props.formRef.current?.setFieldsValue({
      mainGuestPhone: this.mainGuestData('phone'),
    });
  };

  state: IState = {
    isFormValid: false,
  };

  checkFormValidity = debounce(() => {
    if (!this.props.formRef.current) return;
  
    // Get only errors from visible fields (excluding saveCard and country)
    const hasErrors = this.props.formRef.current
      .getFieldsError()
      .some(({ errors }) => errors.length > 0);
  
    // Get the current field values
    const allFields = this.props.formRef.current.getFieldsValue();
  
    // Manually exclude saveCard and country from validation
    const filteredFields = Object.keys(allFields)
      .reduce((obj, key) => {
        obj[key] = allFields[key];
        return obj;
      }, {});
  
    // Check if all visible fields are either user-filled or pre-filled from sessionStorage
    const allFieldsFilled = Object.entries(filteredFields).every(([key, value]) => {
      return value !== undefined && value !== ""; // Ensure the field is filled
    });

  
    // Update the button state
    this.setState({ isFormValid: !hasErrors && allFieldsFilled });
  }, 500);

  componentDidMount() {
    const storedGuestInfo = localStorage.getItem("HOTEL_BOOKING_GUEST_INFO_LABEL");
  
    if (storedGuestInfo) {
      // Parse and set form values if data exists in sessionStorage
      const guestData = storedGuestInfo ? JSON.parse(storedGuestInfo)[0] : {}; // Assume first guest
  
      this.props.formRef.current?.setFieldsValue({
        mainGuestGivenName: guestData.givenName || "",
        mainGuestPhone: guestData.phone || "",
        mainGuestSurname: guestData.surname || "",
      });
  
      // Run validation to ensure form is marked as "valid"
      this.checkFormValidity();
      this.props.handleGuestFormValidation(this.state.isFormValid);
    }
  }

  componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (prevState.isFormValid !== this.state.isFormValid) {
      this.props.handleGuestFormValidation(this.state.isFormValid);
    }
  }

  render(): React.ReactNode {
    const { reviewBookStore } = this.props;
    const { bookingSummary } = reviewBookStore;

    return (
      <div className="guest-info">
        <div className="guest-info__header">
          <h4 className="guest-info__title secondary-font">
            <FormattedMessage id="guest.info" />
          </h4>
          <p className="guest-info__description">
            <FormattedMessage id="guest.checking" />
          </p>
        </div>

        <Form
          name="guest-info-rule"
          ref={this.props.formRef}
          onFieldsChange={this.checkFormValidity}
        >
          {bookingSummary.bookingCard.package.rooms.map((room, index) => (
            <RoomInfo
              key={index}
              room={room}
              indexRoom={index}
              isCheckMainGuest={this.props.isCheckMainGuest}
              onFocusPhone={this.onFocusPhone}
              checkFormValidity={this.checkFormValidity}
              formRef={this.props.formRef}
              emailRef={this.props.emailRef}
              isEmailChanging={this.props.isEmailChanging}
            />
          ))}
        </Form>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    reviewBookStore: state.reviewBookStore,
  };
};

export const GuestInfo = connect(mapStateToProps)(injectIntl(GuestInfoComponent));
